import type { Permissao } from '@locaveri/shared'
import {
  MessageSquareHeart,
  TrendingUp,
  Smile,
  Building2,
  Calculator,
  CalendarCheck,
  CalendarDays,
  Car,
  ClipboardList,
  CreditCard,
  FileSignature,
  FileWarning,
  Gauge,
  GanttChartSquare,
  LayoutDashboard,
  LayoutGrid,
  type LucideIcon,
  MapPin,
  MapPinned,
  PackagePlus,
  Receipt,
  ReceiptText,
  Scale,
  ScanFace,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Tags,
  TicketPercent,
  Users,
} from 'lucide-react'

/**
 * Itens da navegação principal.
 *
 * Duas coisas moram aqui:
 *
 *  1. `para: null` marca módulo que ainda não existe como rota — o item aparece
 *     desabilitado em vez de sumir, para que a estrutura do produto fique
 *     legível. Cada fase troca um `null` pela rota de verdade.
 *
 *  2. `permissao` é o que o menu consulta para decidir se o item existe PARA
 *     ESTA PESSOA. Item sem permissão não aparece — e a rota correspondente
 *     também é protegida (defesa em profundidade; quem decide de verdade é o
 *     servidor). Mostrar "Financeiro" para o papel OPERACIONAL seria oferecer
 *     um caminho que termina em 403.
 */

/** As rotas que o menu pode apontar. O TypeScript confere cada `<Link to>`. */
export type RotaDoMenu =
  | '/'
  | '/usuarios'
  | '/contas'
  | '/frota'
  | '/categorias'
  | '/locais'
  | '/custos'
  | '/precos'
  | '/precos/regras'
  | '/permissoes'
  | '/simulador'
  | '/extras'
  | '/cupons'
  | '/disponibilidade'
  | '/reservas'
  | '/fila-de-aprovacao'
  | '/agenda'
  | '/multas'
  | '/sinistros'
  | '/pagamentos'
  | '/faturas'
  | '/alocacoes/pesos'
  | '/contratos/template'
  | '/vistorias/checklist'
  | '/indicadores'
  | '/ocupacao'
  | '/mapa'
  | '/pesquisa'
  | '/pesquisa/configuracao'
  | '/configuracoes/mira-score'

export interface ItemNavegacao {
  readonly chave: string
  readonly rotulo: string
  readonly icone: LucideIcon
  readonly para: RotaDoMenu | null
  /** `null` = visível para qualquer sessão autenticada. */
  readonly permissao: Permissao | null
}

/**
 * Um grupo do menu. `rotulo: null` é o grupo sem cabeçalho do topo (a visão
 * geral), que não abre nem fecha: é a tela de entrada.
 *
 * Os grupos existem porque o menu passou de trinta itens numa lista só e o QA
 * de 30/10 apontou o óbvio: ninguém acha "Faturas" no meio de "Cupons" e
 * "Sinistros". A ordem dos grupos é a da rotina — primeiro o que o atendimento
 * abre o dia todo, por último o que se ajusta uma vez por mês.
 */
export interface SecaoNavegacao {
  readonly chave: string
  readonly rotulo: string | null
  readonly itens: readonly ItemNavegacao[]
}

export const SECOES_NAVEGACAO: readonly SecaoNavegacao[] = [
  {
    chave: 'inicio',
    rotulo: null,
    itens: [
      {
        chave: 'dashboard',
        rotulo: 'Visão geral',
        icone: LayoutDashboard,
        para: '/',
        permissao: 'dashboard.read',
      },
    ],
  },
  {
    chave: 'reservas',
    rotulo: 'Reservas e clientes',
    itens: [
      {
        chave: 'simulador',
        // "Cotar e reservar" e não "Simulador de preço": a tela deixou de ser só
        // simulação quando ganhou o fechamento da reserva e o link para o cliente
        // (Gap 13). Um atendente que precisa fechar uma reserva não procura por
        // "simulador" no menu.
        rotulo: 'Cotar e reservar',
        icone: Calculator,
        para: '/simulador',
        permissao: 'quote.create',
      },
      {
        chave: 'reservas',
        rotulo: 'Reservas',
        icone: CalendarDays,
        para: '/reservas',
        permissao: 'reservation.read',
      },
      {
        chave: 'fila-de-aprovacao',
        rotulo: 'Fila de aprovação',
        icone: ShieldCheck,
        para: '/fila-de-aprovacao',
        permissao: 'reservation.approve',
      },
      {
        chave: 'agenda',
        rotulo: 'Agenda da frota',
        icone: GanttChartSquare,
        para: '/agenda',
        permissao: 'reservation.read',
      },
      {
        chave: 'disponibilidade',
        rotulo: 'Disponibilidade',
        icone: CalendarCheck,
        para: '/disponibilidade',
        permissao: 'availability.read',
      },
      {
        chave: 'clientes',
        rotulo: 'Clientes',
        icone: Building2,
        para: '/contas',
        permissao: 'account.read',
      },
    ],
  },
  {
    chave: 'frota',
    rotulo: 'Frota',
    itens: [
      { chave: 'frota', rotulo: 'Frota', icone: Car, para: '/frota', permissao: 'vehicle.read' },
      // Logo abaixo da Frota: é a mesma frota, vista de onde está. Quem não tem
      // `telemetry.read` não vê o item — posição de cliente é permissão à parte.
      {
        chave: 'mapa-da-frota',
        rotulo: 'Mapa da frota',
        icone: MapPinned,
        para: '/mapa',
        permissao: 'telemetry.read',
      },
      {
        chave: 'categorias',
        rotulo: 'Categorias',
        icone: LayoutGrid,
        para: '/categorias',
        permissao: 'vehicle_category.read',
      },
      {
        chave: 'locais',
        rotulo: 'Locais',
        icone: MapPin,
        para: '/locais',
        permissao: 'location.read',
      },
      // O item "Operação" (vistorias) saiu do menu na Fase 4: a vistoria vive
      // DENTRO da reserva (`/reservas/:id/vistorias`) — ela não existe sem uma.
      // O CHECKLIST dela, porém, é configuração do tenant e mora aqui.
      {
        chave: 'checklist-de-vistoria',
        rotulo: 'Checklist de vistoria',
        icone: ClipboardList,
        para: '/vistorias/checklist',
        permissao: 'inspection.read',
      },
      {
        chave: 'multas',
        rotulo: 'Multas',
        icone: FileWarning,
        para: '/multas',
        permissao: 'traffic_fine.read',
      },
      {
        chave: 'sinistros',
        rotulo: 'Sinistros',
        icone: ShieldAlert,
        para: '/sinistros',
        permissao: 'incident.read',
      },
      {
        chave: 'pesos-de-alocacao',
        rotulo: 'Pesos de alocação',
        icone: Scale,
        para: '/alocacoes/pesos',
        permissao: 'rental_policy.read',
      },
    ],
  },
  {
    chave: 'precos',
    rotulo: 'Preços',
    itens: [
      {
        chave: 'precos',
        rotulo: 'Tabelas de preço',
        icone: Tags,
        para: '/precos',
        permissao: 'price_table.read',
      },
      {
        chave: 'regras-de-preco',
        rotulo: 'Regras de preço',
        icone: SlidersHorizontal,
        para: '/precos/regras',
        permissao: 'price_table.read',
      },
      {
        chave: 'extras',
        rotulo: 'Extras',
        icone: PackagePlus,
        para: '/extras',
        permissao: 'extra.read',
      },
      {
        chave: 'cupons',
        rotulo: 'Cupons',
        icone: TicketPercent,
        para: '/cupons',
        permissao: 'coupon.read',
      },
    ],
  },
  // O placeholder "Financeiro" da Fase 1 virou dois módulos reais na Fase 5:
  // as transações do provedor e o ciclo mensal de faturas do corporativo.
  {
    chave: 'financeiro',
    rotulo: 'Financeiro',
    itens: [
      {
        chave: 'pagamentos',
        rotulo: 'Pagamentos',
        icone: CreditCard,
        para: '/pagamentos',
        permissao: 'payment.read',
      },
      {
        chave: 'faturas',
        rotulo: 'Faturas',
        icone: ReceiptText,
        para: '/faturas',
        permissao: 'invoice.read',
      },
      {
        chave: 'custos',
        rotulo: 'Custos da frota',
        icone: Receipt,
        para: '/custos',
        permissao: 'vehicle_cost.read',
      },
    ],
  },
  // Leitura do PERÍODO, não cadastro: a home responde "como estamos hoje" e
  // este grupo responde "como fomos". Quem opera vê só a ocupação.
  {
    chave: 'relatorios',
    rotulo: 'Relatórios',
    itens: [
      {
        chave: 'indicadores',
        rotulo: 'Indicadores',
        icone: TrendingUp,
        para: '/indicadores',
        permissao: 'report.read',
      },
      {
        chave: 'ocupacao',
        rotulo: 'Ocupação da frota',
        icone: Gauge,
        para: '/ocupacao',
        permissao: 'fleet_occupancy.read',
      },
    ],
  },
  // A pesquisa tem grupo próprio, ANTES das configurações: ela é leitura de
  // rotina do atendimento (que tem `survey.read`), não um ajuste de sistema.
  // Enterrá-la em "Configurações" faria a única tela que mede qualidade ficar
  // no lugar onde ninguém entra duas vezes por mês.
  {
    chave: 'pesquisa',
    rotulo: 'Pesquisa de satisfação',
    itens: [
      {
        chave: 'pesquisa',
        rotulo: 'Satisfação e NPS',
        icone: Smile,
        para: '/pesquisa',
        permissao: 'survey.read',
      },
      {
        chave: 'pesquisa-configuracao',
        rotulo: 'Configurar a pesquisa',
        icone: MessageSquareHeart,
        para: '/pesquisa/configuracao',
        // `read` e não `update`: quem só lê abre a tela em modo leitura, e é assim
        // que ele confere o corte que está valendo antes de pedir a mudança.
        permissao: 'survey.read',
      },
    ],
  },
  // O antigo item "Configurações" (desabilitado, "chega nas próximas fases")
  // virou este grupo: as telas de ajuste do tenant já existem, e um item morto
  // ao lado delas só fazia o QA clicar e achar que estava quebrado.
  {
    chave: 'configuracoes',
    rotulo: 'Configurações',
    itens: [
      {
        chave: 'usuarios',
        rotulo: 'Usuários',
        icone: Users,
        para: '/usuarios',
        permissao: 'user.read',
      },
      {
        chave: 'permissoes',
        rotulo: 'Perfis e permissões',
        icone: ShieldCheck,
        para: '/permissoes',
        // `role.read` e não `role.update`: quem audita acesso não necessariamente
        // edita, e a tela abre em leitura para quem não tem a escrita.
        permissao: 'role.read',
      },
      {
        chave: 'mira-score',
        rotulo: 'Mira Score',
        icone: ScanFace,
        para: '/configuracoes/mira-score',
        // `read` e não `update`: quem aprova reserva confere se a análise está
        // ligada; trocar a chave pede `credit_analysis.update`, dentro da tela.
        permissao: 'credit_analysis.read',
      },
      {
        chave: 'template-de-contrato',
        rotulo: 'Template de contrato',
        icone: FileSignature,
        para: '/contratos/template',
        permissao: 'tenant.read',
      },
    ],
  },
]

/**
 * A rota aberta pertence a este item? `/` só vale exata (senão tudo seria da
 * visão geral); as demais valem também para as telas de detalhe abaixo delas —
 * `/reservas/123` abre o grupo de Reservas.
 */
export function rotaPertenceAoItem(caminho: string, item: ItemNavegacao): boolean {
  if (item.para === null) return false
  if (item.para === '/') return caminho === '/'
  return caminho === item.para || caminho.startsWith(`${item.para}/`)
}

/** Texto único do item ainda não implementado — não repetir string na UI. */
export const AVISO_MODULO_FUTURO = 'Este módulo chega nas próximas fases.'
